/**
 * Dialog for a product that is sold out for good, offering alternatives.
 *
 * Extends Dawn's ModalDialog (assets/global.js), which supplies the focus trap,
 * Escape handling, body scroll lock, click-outside-to-close and focus
 * restoration. This subclass adds the once-per-session auto-open and the
 * repointed sold-out button in buy-buttons.liquid.
 */
if (!customElements.get('sold-out-modal')) {
  customElements.define(
    'sold-out-modal',
    class SoldOutModal extends ModalDialog {
      connectedCallback() {
        // ModalDialog relocates this element to document.body, which re-runs
        // connectedCallback — so guard before doing any work of our own.
        const alreadyMoved = this.moved;

        // Once relocated to body the element sits outside the section, so a
        // re-render never removes it. Drop the stale twin before we take over,
        // or its duplicate id and click listener would shadow ours.
        if (!alreadyMoved && this.id) {
          document.querySelectorAll(`sold-out-modal[id="${this.id}"]`).forEach((existingModal) => {
            if (existingModal !== this) existingModal.remove();
          });
        }

        super.connectedCallback();
        if (alreadyMoved || this.initialized) return;
        this.initialized = true;

        this.storageKey = this.dataset.storageKey;

        document.addEventListener('click', this.handleTriggerClick);

        if (this.dataset.autoOpen === 'true') this.autoOpen();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleTriggerClick);
        // A removed instance must not still be waiting to show itself.
        window.clearTimeout(this.autoOpenTimeout);
      }

      // Delegated: product-info.js re-renders the buy-buttons area on variant
      // change, so a direct binding to the trigger would go stale.
      handleTriggerClick = (event) => {
        const trigger = event.target.closest('[data-sold-out-modal-trigger]');
        if (!trigger) return;

        event.preventDefault();
        // Opening it by hand is the showing — don't let the timer nag again after.
        window.clearTimeout(this.autoOpenTimeout);
        this.writeSeenFlag();
        this.show(trigger);
      };

      autoOpen() {
        if (this.readSeenFlag()) return;

        const delay = parseInt(this.dataset.autoOpenDelay, 10) || 0;

        this.autoOpenTimeout = window.setTimeout(() => {
          // Give focus somewhere sensible on close; ModalDialog leaves focus on
          // the body when it has no opener to return to.
          const trigger = document.querySelector('[data-sold-out-modal-trigger]');
          this.show(trigger || undefined);
          this.writeSeenFlag();
        }, delay);
      }

      // sessionStorage throws in Safari private mode and some embedded webviews.
      // A failure here must not take the product page down with it.
      readSeenFlag() {
        try {
          return window.sessionStorage.getItem(this.storageKey) !== null;
        } catch (error) {
          return true;
        }
      }

      writeSeenFlag() {
        try {
          window.sessionStorage.setItem(this.storageKey, '1');
        } catch (error) {
          // Storage unavailable — the popup simply shows again next page load.
        }
      }
    },
  );
}
